import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { ReactComponent as HelpSupportIcon } from 'assets/icons/HelpSupportIcon.svg';
import { ReactComponent as PhoneCallIcon } from 'assets/icons/PhoneCallIcon.svg';
import { ReactComponent as EmailIcon } from 'assets/icons/EmailIcon.svg';
import { ReactComponent as LinkIcon } from 'assets/icons/LinkIcon.svg';

import { useStyles } from './styles';

export const HELP_CONTACT_INFO = {
  PHONE: '833-227-0637',
  EMAIL: 'customer.support@fieldca.com',
  SERVICE_DESK_URL: 'https://fccs.atlassian.net/servicedesk/customer/portal/12',
} as const;

export interface IHelpModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<IHelpModalProps> = ({ isVisible, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const Content = (
    <div className={classes.contentContainer}>
      <div className={classes.iconContainer}>
        <HelpSupportIcon />
      </div>
      <h2 className={classes.title}>{t('help.title')}</h2>
      <p className={classes.description}>
        <span className={classes.descriptionDesktop}>
          {t('help.description')
            .split('\n')
            .map((line, idx) => (
              <React.Fragment key={idx}>
                {line}
                {idx === 0 && <br />}
              </React.Fragment>
            ))}
        </span>
        <span className={classes.descriptionMobile}>
          {t('help.descriptionMobile')
            .split('\n')
            .map((line, idx) => (
              <React.Fragment key={idx}>
                {line}
                {idx < 2 && <br />}
              </React.Fragment>
            ))}
        </span>
      </p>
      <div className={classes.contactList}>
        <div className={classes.contactItem}>
          <PhoneCallIcon className={classes.contactIcon} />
          <div className={classes.contactDetails}>
            <div className={classes.phoneContainer}>
              <a href={`tel:${HELP_CONTACT_INFO.PHONE}`} className={classes.contactLink} data-testid="help-phone-link">
                {HELP_CONTACT_INFO.PHONE}
              </a>
              <span className={classes.contactSubtext}>{t('help.phoneHours')}</span>
            </div>
          </div>
        </div>
        <div className={classes.contactItem}>
          <EmailIcon className={classes.contactIcon} />
          <div className={classes.contactDetails}>
            <a
              href={`mailto:${HELP_CONTACT_INFO.EMAIL}`}
              className={classes.contactLink}
              data-testid="help-email-link"
            >
              {HELP_CONTACT_INFO.EMAIL}
            </a>
          </div>
        </div>
        <div className={classes.contactItem}>
          <LinkIcon className={classes.contactIcon} />
          <div className={classes.contactDetails}>
            <a
              href={HELP_CONTACT_INFO.SERVICE_DESK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.contactLink}
              data-testid="help-service-desk-link"
            >
              {HELP_CONTACT_INFO.SERVICE_DESK_URL}
            </a>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      isVisible={isVisible}
      Content={Content}
      modalStyleClass={classes.modal}
      modalContentStyleClass={classes.modalContent}
      disableScrollLock={false}
      testId="help-modal"
      onClose={onClose}
    />
  );
};

export default memo(HelpModal);
